import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import mermaid from 'mermaid';
import { Maximize2, X } from 'lucide-react';

const Diagram = ({ chart }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [svgContent, setSvgContent] = useState(null);
    const [isDarkMode, setIsDarkMode] = useState(() => {
        if (typeof window !== 'undefined') {
            return localStorage.getItem('sd-theme') !== 'light';
        }
        return true;
    });

    useEffect(() => {
        const handleThemeChange = () => {
            const currentTheme = localStorage.getItem('sd-theme');
            setIsDarkMode(currentTheme !== 'light');
        };
        window.addEventListener('sd-theme-changed', handleThemeChange);
        return () => window.removeEventListener('sd-theme-changed', handleThemeChange);
    }, []);

    useEffect(() => {
        let isMounted = true;
        const renderDiagram = async () => {
            if (!chart) return;
            setLoading(true);
            setError(false);
            setSvgContent(null);

            // Dynamically initialize mermaid with the current theme
            mermaid.initialize({
                startOnLoad: false,
                theme: isDarkMode ? 'dark' : 'base',
                securityLevel: 'loose',
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                themeVariables: isDarkMode ? {
                    darkMode: true,
                    background: '#111827',
                    primaryColor: '#1e1b4b',
                    primaryTextColor: '#e2e8f0',
                    primaryBorderColor: '#0ea5e9',
                    lineColor: '#0ea5e9',
                    secondaryColor: '#1a1f2e',
                    tertiaryColor: '#0f172a',
                    noteBkgColor: '#1e1b4b',
                    noteTextColor: '#e2e8f0',
                    noteBorderColor: '#0ea5e9',
                    edgeLabelBackground: '#111827',
                    clusterBkg: '#0f172a',
                    clusterBorder: '#334155',
                    titleColor: '#e2e8f0',
                    actorTextColor: '#e2e8f0',
                    actorBkg: '#1e1b4b',
                    actorBorder: '#0ea5e9',
                    actorLineColor: '#0ea5e9',
                    signalColor: '#0ea5e9',
                    signalTextColor: '#e2e8f0',
                } : {
                    darkMode: false,
                    background: '#f8fafc',
                    primaryColor: '#f0f9ff',
                    primaryTextColor: '#0f172a',
                    primaryBorderColor: '#0ea5e9',
                    lineColor: '#3b82f6',
                    secondaryColor: '#ffffff',
                    tertiaryColor: '#f1f5f9',
                    noteBkgColor: '#e0f2fe',
                    noteTextColor: '#0f172a',
                    noteBorderColor: '#0ea5e9',
                    edgeLabelBackground: '#f8fafc',
                    clusterBkg: '#ffffff',
                    clusterBorder: '#cbd5e1',
                    titleColor: '#0f172a',
                    actorTextColor: '#0f172a',
                    actorBkg: '#f0f9ff',
                    actorBorder: '#0ea5e9',
                    actorLineColor: '#3b82f6',
                    signalColor: '#3b82f6',
                    signalTextColor: '#0f172a',
                }
            });

            try {
                const id = 'mermaid-svg-' + Math.random().toString(36).substring(2, 9);
                const { svg } = await mermaid.render(id, chart);
                if (isMounted) {
                    setSvgContent(svg);
                }
            } catch (err) {
                console.error('Mermaid render error:', err);
                if (isMounted) {
                    setError(true);
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        renderDiagram();

        return () => {
            isMounted = false;
        };
    }, [chart, isDarkMode]);

    // Disable body scroll when fullscreen
    useEffect(() => {
        if (isFullscreen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }

        return () => { document.body.style.overflow = 'auto'; };
    }, [isFullscreen]);

    return (
        <>
            <div
                className="sd-mermaid-container sd-mermaid-clickable"
                onClick={() => !loading && !error && svgContent && setIsFullscreen(true)}
            >
                {loading && <div className="sd-diagram-loading">Loading diagram</div>}
                {error && <p style={{ color: 'var(--sd-text-subtle)', fontSize: '0.85rem' }}>Diagram could not be rendered</p>}

                {!loading && !error && svgContent && (
                    <button className="sd-mermaid-expand-btn" aria-label="View fullscreen">
                        <Maximize2 size={16} />
                    </button>
                )}

                {!loading && !error && svgContent && (
                    <div
                        style={{ display: 'flex', width: '100%', justifyContent: 'center' }}
                        dangerouslySetInnerHTML={{ __html: svgContent }}
                    />
                )}
            </div>

            {/* Fullscreen Modal - using Portal to escape stacking contexts */}
            {isFullscreen && typeof document !== 'undefined' && createPortal(
                <div className="sysdesign-root">
                    <div className="sd-mermaid-fullscreen" onClick={() => setIsFullscreen(false)}>
                        <div className="sd-mermaid-fullscreen-content" onClick={e => e.stopPropagation()}>
                            <button className="sd-mermaid-close-btn" onClick={() => setIsFullscreen(false)}>
                                <X size={24} />
                            </button>
                            <div
                                className="sd-mermaid-fullscreen-svg"
                                dangerouslySetInnerHTML={{ __html: svgContent }}
                            />
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
};

export default Diagram;
